import React, {useEffect, useState} from "react";
import axios from "axios";
import {NavLink} from "react-router-dom";
import * as config from "../../configs/Config";
import {LanguageContextConsumer} from "../context/LanguageContext";
import {adaptUser, persistAuth} from "../../libraries/Authentication";
import {isSpotifyConfigured, spotifyLogin} from "../../libraries/SpotifyAuth";
import AuthShell, {AuthButton, AuthError, AuthInput, AuthLabel} from "./AuthShell";
import {AbGlyph} from "../brand/AbMark";

const Login = (props) => {
    const {setUser, setJwtToken} = props
    const [newLoginCredentials, setNewLoginCredentials] = useState({email: null, password: null})
    const [error, setError] = useState(null);

    const finishAuth = (data) => {
        const adapted = adaptUser(data.user)
        persistAuth(data.token, adapted)
        setJwtToken(data.token)
        setUser(adapted, adapted.referenceId)
        window.location.replace('/')
    }

    const loginHandler = async () => {
        const {email, password} = newLoginCredentials
        if (!email || !password) {
            setError("Please fill the form")
            return;
        }
        try {
            const {data} = await axios.post(`${config.API_URL}/login`, {email, password}, {
                headers: {Accept: 'application/json'}
            })
            finishAuth(data)
        } catch (err) {
            setError(err.response?.data?.message || err.response?.data?.errors?.email?.[0] || err.message)
        }
    }

    // --- Sign in with Apple (identity SSO — separate from the Apple Music
    // playback link). Renders only when the Services ID is configured. ---
    const appleClientId = process.env.REACT_APP_APPLE_CLIENT_ID;
    const [appleReady, setAppleReady] = useState(false);

    useEffect(() => {
        if (!appleClientId) return;
        const s = document.createElement('script');
        s.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
        s.onload = () => {
            window.AppleID.auth.init({
                clientId: appleClientId,
                scope: 'name email',
                redirectURI: window.location.origin,
                usePopup: true,
            });
            setAppleReady(true);
        };
        document.head.appendChild(s);
        return () => { document.head.removeChild(s); };
    }, [appleClientId]);

    const appleLoginHandler = async () => {
        setError(null)
        try {
            const res = await window.AppleID.auth.signIn()
            const name = [res.user?.name?.firstName, res.user?.name?.lastName].filter(Boolean).join(' ') || undefined
            const {data} = await axios.post(`${config.API_URL}/auth/apple`, {
                identity_token: res.authorization.id_token,
                name,
            }, {headers: {Accept: 'application/json'}})
            finishAuth(data)
        } catch (err) {
            if (err?.error === 'popup_closed_by_user') return
            setError(err.response?.data?.message || err.message || 'Apple sign-in failed')
        }
    }

    const setCredential = (key, val) => {
        setError(null)
        setNewLoginCredentials({...newLoginCredentials, [key]: val})
    }

    return (
        <AuthShell bare>
            <div className="flex w-full max-w-[360px] flex-col items-center">
                <NavLink to="/" title="AbTunes">
                    <span className="block sm:hidden"><AbGlyph size={88}/></span>
                    <span className="hidden sm:block"><AbGlyph size={140}/></span>
                </NavLink>

                {appleReady && (
                    <button
                        type="button"
                        onClick={appleLoginHandler}
                        className="mt-8 flex w-full items-center justify-center gap-2.5 rounded-2xl bg-white py-3 text-[15px] sm:mt-12 sm:py-4 font-bold text-[#111] shadow-[0_18px_44px_rgba(0,0,0,0.35)] transition hover:scale-[1.02]"
                    >
                        <svg viewBox="0 0 814 1000" className="h-[18px] w-[18px] fill-current" aria-hidden="true">
                            <path d="M788 341c-6 4-108 62-108 190 0 148 130 200 134 202-1 3-21 71-69 141-43 61-88 123-156 123s-86-40-165-40c-77 0-104 41-167 41s-107-57-157-127C42 786 0 661 0 543c0-190 123-291 245-291 64 0 118 42 159 42 39 0 99-45 172-45 28 0 128 3 212 92zM554 159c32-38 55-91 55-144 0-7-1-15-2-21-52 2-114 35-152 78-29 33-57 86-57 140 0 8 2 16 2 19 4 1 9 2 15 2 47 0 106-32 139-74z"/>
                        </svg>
                        Continue with Apple
                    </button>
                )}
                {appleReady && isSpotifyConfigured() && (
                    <button
                        type="button"
                        onClick={() => spotifyLogin()}
                        className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl bg-[#1DB954] py-4 text-[15.5px] font-bold text-white"
                    >
                        Continue with Spotify
                    </button>
                )}

                <div className="mt-6 w-full rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-2xl sm:mt-8 sm:rounded-3xl sm:p-7">
                        <AuthLabel htmlFor="js-email">Email</AuthLabel>
                        <AuthInput id="js-email" name="email" type="email" onChange={e => setCredential('email', e.target.value)}/>
                        <AuthLabel htmlFor="js-password">Password</AuthLabel>
                        <AuthInput id="js-password" name="password" type="password" onChange={e => setCredential('password', e.target.value)}/>
                        <div className="mt-2 flex justify-end">
                            <NavLink to="/forgot-password" className="text-xs font-semibold text-white/40 hover:text-white/70">
                                Forgot password?
                            </NavLink>
                        </div>
                        <AuthButton onClick={loginHandler} type="submit">Log in</AuthButton>
                    </div>

                <AuthError error={error}/>
                <p className="mt-5 text-center text-[13px] sm:mt-7">
                    <NavLink to="/register" className="font-bold text-white hover:text-apple-red">Create account</NavLink>
                </p>
            </div>
        </AuthShell>
    );
};

export default (props) => <LanguageContextConsumer Component={Login} {...props} />;
